import { data, type ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'

/**
 * App-level modal stack. Any caller (overflow menu, detail page,
 * dialog) can open a modal by registering a render thunk that
 * returns a fully-built node — the AppView mounts a single host
 * (`ModalLayer`) that subscribes to this stack and shows the top
 * entry.
 *
 * Centralizing the stack here avoids per-caller portals (the
 * pattern that broke share modals inside the virtualized track
 * list — when rows recycled, the portalled modal got orphaned in
 * `<body>` and a follow-up Share click hit a stale instance with
 * no visible UI). The Modal element itself still owns its open /
 * close transitions; this just owns its lifetime.
 *
 * Usage:
 *   openModal({ render: () => <ConfirmModal {...} /> })
 *
 * The returned `close` function is wired into the modal's onClose
 * handler so callers don't have to thread close down themselves.
 */

interface ModalEntry {
    readonly id: number
    readonly node: Node
}

const stack = data<readonly ModalEntry[]>([])
let nextId = 1

export interface OpenModalOptions {
    /**
     * Builds the modal's DOM node. Receives a `close` callback the
     * caller is expected to wire into the modal's `onClose` so the
     * stack can drop this entry. The thunk runs once per open.
     */
    readonly render: (close: () => void) => Node
}

export interface OpenModalHandle {
    /** Programmatically dismiss the modal. */
    readonly close: () => void
}

export function openModal(options: OpenModalOptions): OpenModalHandle {
    const id = nextId++
    const close = () => {
        const next = stack.value.filter((entry) => entry.id !== id)
        if (next.length !== stack.value.length) stack.value = next
    }
    const node = options.render(close)
    stack.value = [...stack.value, { id, node }]
    return { close }
}

/**
 * Returns the topmost modal node, or null when nothing is open.
 * The ModalLayer host swaps its single child against this value.
 */
export function getTopModal(): ObservableValue<Maybe<Node>> {
    return stack.map((entries) => (entries.length ? entries[entries.length - 1].node : null))
}

/** Drops every modal — useful as a panic close on hard route changes. */
export function clearModalStack(): void {
    stack.value = []
}
